/**
 * Skill → category slug mapping (legacy PDL-style taxonomy).
 * Source data: `skill-categories-map.json` (generated from the canonical SKILL_CATEGORIES map).
 */

import type { IconName } from "@/components/ui/icon";
import skillCategoriesMap from "./categories-map.json";

export type SkillCategorySlug =
  | "web3"
  | "testing"
  | "systems_admin"
  | "network_security"
  | "mobile"
  | "front_end"
  | "devops"
  | "databases"
  | "data_science"
  | "cloud_technologies"
  | "back_end"
  | "ai_ml"
  | "other";

export const CATEGORY_LABELS: Record<SkillCategorySlug, string> = {
  web3: "Web3",
  testing: "Testing",
  systems_admin: "Systems Admin",
  network_security: "Network & Security",
  mobile: "Mobile",
  front_end: "Front-End",
  devops: "DevOps",
  databases: "Databases",
  data_science: "Data Science",
  cloud_technologies: "Cloud Technologies",
  back_end: "Back-End",
  ai_ml: "AI / ML",
  other: "Additional Skills",
};

/** Icon + label/icon colors per category (dark UI). */
export const SKILL_CATEGORY_VISUAL: Record<
  SkillCategorySlug,
  { icon: IconName; labelClass: string; iconClass: string }
> = {
  web3: { icon: "IconWeb3", labelClass: "text-[#c4b5fd]", iconClass: "text-[#c4b5fd]" },
  testing: { icon: "IconTestTube", labelClass: "text-[#4ade80]", iconClass: "text-[#4ade80]" },
  systems_admin: { icon: "IconSettingsGear2", labelClass: "text-[#fbbf24]", iconClass: "text-[#fbbf24]" },
  network_security: { icon: "IconShield", labelClass: "text-[#60a5fa]", iconClass: "text-[#60a5fa]" },
  mobile: { icon: "IconDevices", labelClass: "text-[#f472b6]", iconClass: "text-[#f472b6]" },
  front_end: { icon: "IconLayoutWindow", labelClass: "text-[#38bdf8]", iconClass: "text-[#38bdf8]" },
  devops: { icon: "IconCloudySparkle", labelClass: "text-[#22d3ee]", iconClass: "text-[#22d3ee]" },
  databases: { icon: "IconStorage", labelClass: "text-[#34d399]", iconClass: "text-[#34d399]" },
  data_science: { icon: "IconChart2", labelClass: "text-[#c084fc]", iconClass: "text-[#c084fc]" },
  cloud_technologies: { icon: "IconCloud", labelClass: "text-[#93c5fd]", iconClass: "text-[#93c5fd]" },
  back_end: { icon: "IconServer", labelClass: "text-[#fb923c]", iconClass: "text-[#fb923c]" },
  ai_ml: { icon: "IconBrain1", labelClass: "text-[#e879f9]", iconClass: "text-[#e879f9]" },
  other: { icon: "IconListSparkle", labelClass: "text-[#a1a1aa]", iconClass: "text-[#a1a1aa]" },
};

export function getSkillCategoryVisual(slug: SkillCategorySlug) {
  return SKILL_CATEGORY_VISUAL[slug];
}

const RAW = skillCategoriesMap as Record<string, SkillCategorySlug>;

/** Typo alias in source map */
const SKILL_CATEGORIES: Record<string, SkillCategorySlug> = {
  ...RAW,
  "c (programming language)": RAW["c (programming language"] ?? "back_end",
};

export function getSkillCategory(skillName: string): SkillCategorySlug {
  const key = skillName.trim().toLowerCase();
  return SKILL_CATEGORIES[key] ?? "other";
}

export type GroupedSkillCategory = {
  id: string;
  slug: SkillCategorySlug;
  name: string;
  skills: string[];
};

/** Groups raw skill strings by category; preserves original casing per skill; "other" last. */
export function groupSkillsByCategory(skills: string[]): GroupedSkillCategory[] {
  const map = new Map<SkillCategorySlug, string[]>();

  for (const raw of skills) {
    const s = raw.trim();
    if (!s) continue;
    const slug = getSkillCategory(s);
    if (!map.has(slug)) map.set(slug, []);
    map.get(slug)!.push(s);
  }

  for (const [, list] of map) {
    list.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }

  const result: GroupedSkillCategory[] = [];
  for (const [slug, skillList] of map) {
    result.push({
      id: slug,
      slug,
      name: CATEGORY_LABELS[slug],
      skills: skillList,
    });
  }

  result.sort((a, b) => {
    if (a.slug === "other") return 1;
    if (b.slug === "other") return -1;
    return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
  });

  return result;
}
